/**
 * EditCourseForm
 *
 * Settings-tab content for the course editor. Owns the basic course
 * metadata: type, publish state, title, thumbnail, description, tags, and
 * the type-specific fields (External URL/Event Date for events, linear
 * progress toggle for standard courses).
 *
 * Detail-page content lives in `CourseDetailPageTab`. Lesson management
 * lives in `CourseCurriculumTab`. Completion settings live in
 * `CourseCompletionTab`. Each tab persists independently.
 *
 * Layout uses Card sections to give the form visual hierarchy:
 *   - Basics (title, description)
 *   - Visuals (thumbnail)
 *   - External details (External courses only)
 *   - Behavior (linear progress) (Standard courses only)
 *   - Categorization (tags)
 */

import { useSnackbar } from 'notistack';
import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Tag, TagType, CourseType, UserRole, Course } from '../../apis/api-client';
import { getFileUrl, uploadFile, alert } from '../../hooks/function';
import { RootState } from '../../types';
import { tagScopeForUser } from '../../utils/tagFilters';
import { isAccountOrSystemAdmin } from '../../utils/roleHelpers';
import { CourseAuthor } from '../../apis/api-client';
import { selectAuthorById, useEnsureAuthors } from '../../features/lookups/lookupsSlice';
import { getInstructorMessaging } from '../../utils/coursePrefs';
import { courseCustomFieldsClient } from '../../apis/client-instances';
import CourseAuthorAutocomplete from './CourseAuthorAutocomplete';
import CourseCustomFieldsSection from './CourseCustomFieldsSection';
import DeleteCourseConfirmDialog from './DeleteCourseConfirmDialog';
import SectionLabel from './editCourseForm/SectionLabel';
import ThumbnailPicker from './editCourseForm/ThumbnailPicker';
import CourseVerificationSection from './editCourseForm/CourseVerificationSection';
import InstructorMessagingField from './editCourseForm/InstructorMessagingField';
import {
  DEFAULT_VERIFICATION,
  EventVerificationConfig,
  getEventVerification,
} from '../../utils/eventVerification';
import { courseToSettings } from '../../utils/courseSettings';
import { TagAutocomplete } from '../common';
import {
  Box,
  Button,
  Card,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  TextField,
  Typography,
} from '../ui';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

export interface EditCourseFormProps {
  /** Existing course to edit, or null when creating. */
  course: Course | null;
  /** Pre-select a type when creating (e.g., LearningPath from the menu). */
  defaultCourseType?: CourseType;
  /** Called with the form payload — parent handles the API call + redux +
   *  navigation. Should return the saved course (with id). */
  onSave: (payload: any) => Promise<any>;
  /** Called when the user clicks Cancel. */
  onCancel: () => void;
  /** Optional delete handler. Only rendered (via the Danger Zone) when a
   *  course exists AND the current user is a Super Admin. */
  onDelete?: (id: number) => Promise<void> | void;
}

export default function EditCourseForm({ course, defaultCourseType, onSave, onCancel, onDelete }: EditCourseFormProps) {
  const [formData, setFormData] = useState({
    title: '',
    thumbnail: '',
    thumbnailUrl: '',
    description: '',
    tagIds: [] as number[],
    courseType: CourseType.Standard,
    externalUrl: '',
    eventDate: '' as string,
    isDraft: true,
  });
  const [selectedThumbnail, setSelectedThumbnail] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [errors, setErrors] = useState({} as any);
  const [loading, setLoading] = useState(false);
  const [selectedTags, setSelectedTags] = useState<Tag[]>([]);
  const [selectedAuthor, setSelectedAuthor] = useState<CourseAuthor | null>(null);
  const [courseCode, setCourseCode] = useState('');
  // Resolve the course's authorId to the full record for the picker. Loaded
  // once; the effect below syncs it into editable state.
  useEnsureAuthors();
  const linkedAuthor = useSelector((state: RootState) => selectAuthorById(state, course?.authorId));
  // Per-course custom-field values, keyed by numeric fieldId (value is
  // always a string[] — text/number hold a single element). Loaded async
  // below; `loadedFieldIds` remembers which fields had a value at load so
  // the save can clear ones the admin emptied out.
  const [customFieldValues, setCustomFieldValues] = useState<Record<number, string[]>>({});
  const loadedFieldIds = useRef<number[]>([]);
  const [instructorMessagingEnabled, setInstructorMessagingEnabled] = useState(false);
  const [instructorMessagingEmail, setInstructorMessagingEmail] = useState('');
  // Event verification config — local-storage backed per slug. Only
  // surfaces in the UI when courseType === External, but the state is
  // hydrated unconditionally so toggling between Standard ↔ Event
  // mid-edit doesn't lose previously-saved verification settings.
  const [verification, setVerification] = useState<EventVerificationConfig>(DEFAULT_VERIFICATION);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  // Track whether the form has unsaved changes — drives the dirty indicator
  // in the action bar.
  const [dirty, setDirty] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const currentUser = useSelector((state: RootState) => state.profile.user);
  const canSeeTags = isAccountOrSystemAdmin(currentUser?.role);
  // Course deletion follows the SAME scope as editing (see useCourseManagement
  // `canEdit`): Super Admin deletes any course, Account Admin any course in
  // their account, Workspace Admin only courses in their own workspace. The
  // client gate is cosmetic — the backend must enforce the same downward-only
  // scope on the delete endpoint.
  const canDeleteCourse =
    currentUser?.role === UserRole.Admin ||
    currentUser?.role === UserRole.AccountAdmin ||
    (currentUser?.role === UserRole.WorkspaceAdmin &&
      (course as any)?.workspaceId === currentUser?.workspaceId);

  useEffect(() => {
    if (course) {
      setFormData({
        title: course.title || '',
        thumbnailUrl: (course as any).thumbnailUrl || '',
        thumbnail: course.thumbnail || '',
        description: course.description || '',
        tagIds: course.tagIds || [],
        courseType: course.courseType || CourseType.Standard,
        externalUrl: course.externalUrl || '',
        eventDate: (course.eventDate as any) || '',
        isDraft: course.isDraft ?? true,
      });
      setImagePreview((course as any).thumbnailUrl ?? null);
      setSelectedTags((course.tags as Tag[] | undefined) ?? []);
      // Author is synced from the linkedAuthor selector by the effect below
      // (the directory may still be loading here). Course code reads off the
      // course; custom fields + messaging hydrate below.
      setCourseCode(course.courseCode ?? '');
      setVerification(getEventVerification(course.eventVerification));
      const messaging = getInstructorMessaging(course);
      setInstructorMessagingEnabled(messaging.enabled);
      setInstructorMessagingEmail(messaging.email);
    } else {
      setFormData({
        title: '',
        thumbnailUrl: '',
        thumbnail: '',
        description: '',
        tagIds: [],
        courseType: defaultCourseType || CourseType.Standard,
        externalUrl: '',
        eventDate: '',
        isDraft: true,
      });
      setImagePreview(null);
      setSelectedTags([]);
      setSelectedAuthor(null);
      setCourseCode('');
      setCustomFieldValues({});
      setVerification(DEFAULT_VERIFICATION);
      setInstructorMessagingEnabled(false);
      setInstructorMessagingEmail('');
    }
    setDirty(false);
    setSelectedThumbnail(null);
  }, [course, defaultCourseType]);

  // Hydrate per-course custom-field values from the API (keyed by numeric
  // courseId). Kept separate from the main hydration effect because it's
  // async. Setting state here must NOT mark the form dirty.
  useEffect(() => {
    if (!course?.id) {
      setCustomFieldValues({});
      loadedFieldIds.current = [];
      return;
    }
    let cancelled = false;
    courseCustomFieldsClient
      .getValues(course.id)
      .then((rows) => {
        if (cancelled) return;
        const map: Record<number, string[]> = {};
        for (const r of rows) {
          if (r.fieldId != null) map[r.fieldId] = r.value ?? [];
        }
        setCustomFieldValues(map);
        loadedFieldIds.current = Object.keys(map).map(Number);
      })
      .catch(() => {
        if (cancelled) return;
        setCustomFieldValues({});
        loadedFieldIds.current = [];
      });
    return () => {
      cancelled = true;
    };
  }, [course?.id]);

  // Sync the editable author selection from the resolved link. Re-keys on the
  // course (so switching courses resets it) and fills in once the directory
  // loads (linkedAuthor: undefined → record). An in-progress pick isn't
  // clobbered: linkedAuthor only changes identity on save or a directory edit.
  useEffect(() => {
    setSelectedAuthor(linkedAuthor ?? null);
  }, [linkedAuthor, course?.id]);

  // Wrap field updates so we can mark the form dirty in one place.
  const updateField = (patch: Partial<typeof formData>) => {
    setFormData((prev) => ({ ...prev, ...patch }));
    setDirty(true);
  };
  const handleChange = (e: any) => {
    setErrors((prev: any) => ({ ...prev, [e.target.name]: '' }));
    updateField({ [e.target.name]: e.target.value } as any);
  };

  const handleThumbnailSelected = (file: File) => {
    setSelectedThumbnail(file);
    setImagePreview(URL.createObjectURL(file));
    setErrors((prev: any) => ({ ...prev, thumbnail: '' }));
    setDirty(true);
  };

  const handleTagsChange = (newTags: Tag[]) => {
    setSelectedTags(newTags);
    const tagIds = newTags.map((tag) => tag.id).filter((id): id is number => id !== undefined);
    updateField({ tagIds });
  };

  const handleAuthorChange = (next: CourseAuthor | null) => {
    setSelectedAuthor(next);
    setDirty(true);
  };

  const validate = () => {
    const newErrors: any = {};
    if (!formData.title.trim()) newErrors.title = 'Title is required';
    if (!formData.description.trim()) newErrors.description = 'Description is required';
    if (!selectedThumbnail && !course?.thumbnail) newErrors.thumbnail = 'Thumbnail is required';
    if (formData.courseType === CourseType.External && !formData.externalUrl?.trim()) {
      newErrors.externalUrl = 'External URL is required for external events';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSaveClick = async (publish?: boolean) => {
    if (!validate()) return;
    setLoading(true);
    try {
      const result = selectedThumbnail ? await uploadFile(selectedThumbnail) : null;

      const updatedFormData = {
        ...formData,
        description: formData.description,
        // Save & Publish forces isDraft=false; otherwise honor the current
        // form state (which preserves Draft on subsequent edits).
        isDraft: publish ? false : formData.isDraft,
        thumbnail: selectedThumbnail ? result?.uploadedUrl : course?.thumbnail,
        thumbnailUrl: selectedThumbnail ? await getFileUrl(result?.uploadedUrl) : (course as any)?.thumbnailUrl,
        eventDate: formData.eventDate || null,
        // Author, course code, instructor messaging, and event
        // verification all persist on the course settings blob. Rebuild
        // the full settings from the loaded course so we don't clobber
        // fields the Detail-page tab owns (CE info, etc.).
        settings: {
          ...courseToSettings(course),
          authorId: selectedAuthor?.id,
          courseCode: courseCode.trim() || undefined,
          instructorMessagingEnabled,
          instructorMessagingEmail,
          eventVerification: verification,
        },
      };
      // Keep the locally-displayed description clean (no marker) so
      // re-opening the form doesn't show JSON in the textarea.
      setFormData({ ...updatedFormData, description: formData.description } as any);
      const saved = await onSave(updatedFormData);
      const targetCourseId = course?.id ?? saved?.id;
      if (targetCourseId) {
        // Author, course code, instructor messaging, and event verification
        // now persist server-side via the settings payload (above). CE
        // Information lives on the "Course detail" tab — see CourseDetailPageTab.
        // Persist custom-field values per field. Union the current fields
        // with the ones present at load so values the admin emptied get
        // cleared (an empty array clears the row server-side).
        const allFieldIds = new Set<number>([
          ...Object.keys(customFieldValues).map(Number),
          ...loadedFieldIds.current,
        ]);
        await Promise.all(
          Array.from(allFieldIds).map((fieldId) =>
            courseCustomFieldsClient.setValue(targetCourseId, {
              fieldId,
              value: customFieldValues[fieldId] ?? [],
            }),
          ),
        );
        loadedFieldIds.current = Object.keys(customFieldValues).map(Number);
        // Verifiable success toast — names the instructor that actually
        // persisted so the admin doesn't have to take "All changes saved"
        // on faith.
        const instructorMsg = selectedAuthor
          ? `Instructor: ${selectedAuthor.name ?? 'assigned'}.`
          : 'No instructor assigned to this course.';
        alert(
          enqueueSnackbar,
          publish
            ? `Saved and published. ${instructorMsg}`
            : `Changes saved. ${instructorMsg}`,
          'success',
        );
      } else {
        // Defensive — should never happen on an edit flow, but if the
        // backend save returned without an id we'd silently drop the
        // custom-field writes. Surface it loudly so the admin can retry
        // instead of thinking the save succeeded.
        alert(
          enqueueSnackbar,
          'Saved, but a course id was not returned — instructor and other settings did not persist. Refresh and try again.',
          'error',
        );
      }
      setDirty(false);
    } catch {
      alert(enqueueSnackbar, 'Save failed', 'error');
    } finally {
      setLoading(false);
    }
  };

  let typeTitle = 'Course Title';
  let saveText = 'Create Course';
  if (formData.courseType === CourseType.LearningPath) {
    typeTitle = 'Learning Path Title';
    saveText = 'Create Learning Path';
  }
  // When editing, the primary action becomes "Save Changes" regardless of type.
  const primarySaveLabel = course ? 'Save Changes' : saveText;
  const showSaveAndPublish = formData.isDraft; // only meaningful if currently a draft

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
      <LocalizationProvider dateAdapter={AdapterDateFns}>

        {/* ── Course Type selector — create mode only ────────────────── */}
        {!course && !defaultCourseType && (
          <Card sx={{ p: 3 }}>
            <SectionLabel title="What are you creating?" />
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Box
                onClick={() => updateField({ courseType: CourseType.Standard, isDraft: true })}
                sx={{
                  flex: 1,
                  minWidth: 160,
                  p: 2,
                  border: '2px solid',
                  borderColor: formData.courseType === CourseType.Standard ? 'primary.main' : 'divider',
                  borderRadius: 2,
                  cursor: 'pointer',
                  bgcolor: formData.courseType === CourseType.Standard ? 'action.selected' : 'transparent',
                  '&:hover': { borderColor: 'primary.main' },
                }}
              >
                <Typography variant="body2" fontWeight={700}>Standard Course</Typography>
                <Typography variant="caption" color="text.secondary">Lessons and modules</Typography>
              </Box>
              <Box
                onClick={() => updateField({ courseType: CourseType.External, isDraft: false })}
                sx={{
                  flex: 1,
                  minWidth: 160,
                  p: 2,
                  border: '2px solid',
                  borderColor: formData.courseType === CourseType.External ? 'primary.main' : 'divider',
                  borderRadius: 2,
                  cursor: 'pointer',
                  bgcolor: formData.courseType === CourseType.External ? 'action.selected' : 'transparent',
                  '&:hover': { borderColor: 'primary.main' },
                }}
              >
                <Typography variant="body2" fontWeight={700}>External Event</Typography>
                <Typography variant="caption" color="text.secondary">Link to external resource</Typography>
              </Box>
            </Box>
          </Card>
        )}

        {/* ── Basics ──────────────────────────────────────────────────── */}
        <Card sx={{ p: 3 }}>
          <SectionLabel
            title="Basics"
            subtitle="The headline and description learners see when browsing."
          />
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            {/* Title + Course ID share a row — Course ID is short enough
                that giving it a whole row was wasted vertical space.
                Auto-wrap on narrow widths via the parent's flexWrap. */}
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <TextField
                label={typeTitle}
                name="title"
                value={formData.title}
                onChange={handleChange}
                required
                error={!!errors.title}
                helperText={errors.title}
                sx={{ flex: '2 1 320px' }}
              />
              <TextField
                label="Course ID"
                value={courseCode}
                onChange={(e) => {
                  setCourseCode(e.target.value);
                  setDirty(true);
                }}
                placeholder="e.g. HIPAA-2025, DEN-101"
                helperText="Optional internal identifier."
                sx={{ flex: '1 1 180px' }}
              />
            </Box>
            <TextField
              label="Description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              multiline
              required
              error={!!errors.description}
              helperText={errors.description || 'What will learners gain from this course?'}
              rows={5}
              fullWidth
            />
            <CourseAuthorAutocomplete
              value={selectedAuthor}
              onChange={handleAuthorChange}
            />

            {/* Instructor messaging — lets learners email questions about
                the course directly to the instructor. Required for CE
                accreditation in many cases. Remounts per-course (key) so its
                local chip/draft UI state resets when the edited course changes. */}
            <InstructorMessagingField
              key={course?.id ?? 'new'}
              enabled={instructorMessagingEnabled}
              email={instructorMessagingEmail}
              onEnabledChange={(next) => {
                setInstructorMessagingEnabled(next);
                setDirty(true);
              }}
              onEmailChange={(next) => {
                setInstructorMessagingEmail(next);
                setDirty(true);
              }}
            />
          </Box>
        </Card>

        {/* ── External event fields ──────────────────────────────────── */}
        {formData.courseType === CourseType.External && (
          <Card sx={{ p: 3 }}>
            <SectionLabel
              title="Event details"
              subtitle="Where and when the external event happens."
            />
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
              <TextField
                label="External URL"
                name="externalUrl"
                value={formData.externalUrl}
                onChange={handleChange}
                required
                error={!!errors.externalUrl}
                helperText={errors.externalUrl || 'Full URL to the event or resource'}
                placeholder="https://..."
                fullWidth
              />
              <DateTimePicker
                label="Event Date"
                value={formData.eventDate ? new Date(formData.eventDate) : null}
                onChange={(newValue) => {
                  if (!newValue) {
                    updateField({ eventDate: '' });
                    return;
                  }
                  const year = newValue.getFullYear();
                  const month = String(newValue.getMonth() + 1).padStart(2, '0');
                  const day = String(newValue.getDate()).padStart(2, '0');
                  const hours = String(newValue.getHours()).padStart(2, '0');
                  const minutes = String(newValue.getMinutes()).padStart(2, '0');
                  const seconds = String(newValue.getSeconds()).padStart(2, '0');
                  updateField({ eventDate: `${year}-${month}-${day}T${hours}:${minutes}:${seconds}` });
                }}
                slotProps={{
                  textField: { fullWidth: true, helperText: 'When does the external event occur?' },
                }}
              />
            </Box>
          </Card>
        )}

        {/* ── Attendance verification (events only) ───────────────────
            How the LMS confirms the learner actually attended.
            Multiple methods can be enabled — any one satisfied marks
            completion (OR-logic). Two methods active today, two
            disabled placeholders for upcoming backend work. */}
        {formData.courseType === CourseType.External && (
          <CourseVerificationSection verification={verification} onChange={setVerification} />
        )}

        {/* ── Visuals + Custom Fields ────────────────────────────────
            Sit side-by-side on wider screens — the thumbnail is short
            and the custom-fields card was a tall column underneath. Now
            they share a row. `auto-fit` stacks them on narrow widths;
            `alignItems: flex-start` lets each card size to its own
            content (no forced equal-height). */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
            gap: 2.5,
            alignItems: 'flex-start',
          }}
        >
          <Card sx={{ p: 3 }}>
            <SectionLabel
              title="Visuals"
              subtitle="Thumbnail shown across the catalog and learner views."
            />
            <ThumbnailPicker
              previewUrl={imagePreview}
              onFileSelected={handleThumbnailSelected}
              error={errors.thumbnail}
            />
          </Card>

          <Card sx={{ p: 3 }}>
            <SectionLabel
              title="Custom Fields"
              subtitle="Catalog attributes like CE hours, category, or role — used as filters in the catalog."
            />
            <CourseCustomFieldsSection
              values={customFieldValues}
              onChange={(next) => {
                setCustomFieldValues(next);
                setDirty(true);
              }}
            />
          </Card>
        </Box>

        {/* ── Categorization ──────────────────────────────────────────── */}
        {canSeeTags && (
          <Card sx={{ p: 3 }}>
            <SectionLabel
              title="Content Tag Provisioning"
              subtitle="Tag the course so workspaces and groups can grant access by tag — see each workspace's Access tab."
            />
            <TagAutocomplete
              multiple
              value={selectedTags}
              onChange={(v) => handleTagsChange((v as Tag[]) ?? [])}
              tagType={TagType.CourseTag}
              {...tagScopeForUser(currentUser?.role, currentUser?.workspaceId)}
              size="medium"
              fullWidth
              placeholder="Select tags"
              helperText="Pick from existing tags. To create new tags, visit the Tags page."
            />
          </Card>
        )}

        {/* ── Danger zone — Super Admins only, edit mode only ────────
            Visually separated with a red-tinted border so it doesn't blend
            in with regular form sections. Modeled on GitHub's repo-settings
            Danger Zone pattern. */}
        {course && onDelete && canDeleteCourse && (
          <Card
            sx={{
              p: 3,
              border: '1px solid',
              borderColor: '#fecaca',
              bgcolor: '#fff8f8',
            }}
          >
            <SectionLabel
              title="Danger zone"
              subtitle="Permanent actions. Take care — these can't be undone."
            />
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 2,
                flexWrap: 'wrap',
              }}
            >
              <Box sx={{ flex: 1, minWidth: 220 }}>
                <Typography variant="body2" fontWeight={600}>
                  Delete this course
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Once deleted, the course and all its lessons, enrollments,
                  and progress data can't be recovered.
                </Typography>
              </Box>
              <Button
                color="error"
                variant="outlined"
                onClick={() => setDeleteOpen(true)}
                disabled={deleting}
              >
                Delete course
              </Button>
            </Box>
          </Card>
        )}

      </LocalizationProvider>

      {/* ── Action bar ─────────────────────────────────────────────────
          Sticky bottom strip with dirty-state indicator and primary
          actions. Keeping it inline-sticky (vs. position:fixed) means it
          stays attached to the form column and doesn't overlap the
          metadata sidebar. */}
      <Box
        sx={{
          position: 'sticky',
          bottom: 0,
          mt: 2,
          py: 1.5,
          px: 2,
          bgcolor: 'background.paper',
          borderTop: 1,
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          // Subtle shadow lifts the bar visually above the form when there's
          // content above it — modeled on Linear / Notion's bottom bars.
          boxShadow: '0 -4px 12px rgba(0,0,0,0.04)',
          zIndex: 1,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
          {dirty ? (
            <>
              <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'warning.main' }} />
              <Typography variant="body2" color="text.secondary">
                Unsaved changes
              </Typography>
            </>
          ) : course ? (
            <Typography variant="body2" color="text.secondary">
              All changes saved
            </Typography>
          ) : null}
          {!course && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={!formData.isDraft}
                  onChange={(e) => updateField({ isDraft: !e.target.checked })}
                  color="primary"
                />
              }
              label={<Typography variant="body2">Publish on create</Typography>}
              sx={{ ml: 0 }}
            />
          )}
        </Box>

        <Button onClick={onCancel} color="inherit">
          Cancel
        </Button>

        {/* Edit mode: show "Save & Publish" alongside Save when draft. */}
        {course && showSaveAndPublish && (
          <Button
            onClick={() => handleSaveClick(true)}
            color="primary"
            variant="outlined"
            disabled={loading}
          >
            Save & Publish
          </Button>
        )}

        <Button
          onClick={() => handleSaveClick(false)}
          variant="contained"
          color="primary"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={18} color="inherit" /> : null}
        >
          {loading ? 'Saving...' : primarySaveLabel}
        </Button>
      </Box>

      <DeleteCourseConfirmDialog
        open={deleteOpen}
        courseTitle={course?.title ?? ''}
        loading={deleting}
        onCancel={() => setDeleteOpen(false)}
        onConfirm={async () => {
          if (!course?.id || !onDelete) return;
          setDeleting(true);
          try {
            await onDelete(course.id);
            // On success, parent typically navigates away; if it doesn't,
            // close the modal so we don't leave it stuck open.
            setDeleteOpen(false);
          } finally {
            setDeleting(false);
          }
        }}
      />
    </Box>
  );
}
